// # Filename: src/components/poker/shared/PlayerAvatarFace.jsx
// Hand-drawn avatar face used by both desktop seat pods and mobile seat markers.

import React from "react";

const PLAYER_AVATAR_PALETTE = [
  { visor: "#0e7490", band: "#67e8f9", vest: "#083344", trim: "#22d3ee" },
  { visor: "#065f46", band: "#34d399", vest: "#052e2b", trim: "#6ee7b7" },
  { visor: "#7f1d1d", band: "#fb7185", vest: "#3f1518", trim: "#fda4af" },
  { visor: "#312e81", band: "#a78bfa", vest: "#1e1b4b", trim: "#c4b5fd" },
  { visor: "#78350f", band: "#fbbf24", vest: "#451a03", trim: "#fde68a" },
  { visor: "#334155", band: "#94a3b8", vest: "#111827", trim: "#cbd5e1" },
  { visor: "#14532d", band: "#84cc16", vest: "#0f2419", trim: "#bef264" },
  { visor: "#581c87", band: "#e879f9", vest: "#2e1065", trim: "#f0abfc" },
];

const HERO_AVATAR_COLORS = {
  visor: "#92400e",
  band: "#fbbf24",
  vest: "#1f2937",
  trim: "#fde68a",
};

const HERO_MOBILE_AVATAR_COLORS = {
  visor: "#0f766e",
  band: "#fde68a",
  vest: "#78350f",
  trim: "#fef3c7",
};

const PLAYER_AVATAR_FACE = {
  eyeY: 26.2,
  eyeHighlightY: 25.8,
  brimTopY: 18.6,
  brimBottomY: 21.6,
  brimTrimY: 21.2,
};

function avatarColorsForSeat(seat, isMe = false) {
  if (isMe) return HERO_AVATAR_COLORS;
  const seatNumber = Math.max(1, Number(seat) || 1);
  return PLAYER_AVATAR_PALETTE[(seatNumber - 1) % PLAYER_AVATAR_PALETTE.length];
}

export default function PlayerAvatarFace({ seat, isMe = false, size = "default" }) {
  const colors = isMe && size === "mobile" ? HERO_MOBILE_AVATAR_COLORS : avatarColorsForSeat(seat, isMe);
  const sizeClassName = size === "mobile" ? "h-[30px] w-[30px]" : "h-[clamp(30px,3.6cqw,46px)] w-[clamp(30px,3.6cqw,46px)]";
  return (
    <svg
      viewBox="0 0 52 52"
      aria-hidden="true"
      className={[sizeClassName, "shrink-0 drop-shadow-[0_7px_12px_rgba(0,0,0,0.28)]"].join(" ")}
    >
      <circle cx="26" cy="26" r="23" fill="rgba(2,6,23,0.78)" stroke={colors.trim} strokeWidth="1.6" />
      <path d="M12 46C13 36 18 31 26 31C34 31 39 36 40 46Z" fill={colors.vest} />
      <path d="M18 33L26 43L34 33C32 31.8 29.4 31.2 26 31.2C22.6 31.2 20 31.8 18 33Z" fill="#f8fafc" />
      <path d="M20 34L26 39L22 42Z" fill="#e2e8f0" />
      <path d="M32 34L26 39L30 42Z" fill="#e2e8f0" />
      <path d="M13 46C14.5 38 18.2 33.5 23 32L26 46Z" fill={colors.vest} />
      <path d="M39 46C37.5 38 33.8 33.5 29 32L26 46Z" fill={colors.vest} />
      <circle cx="26" cy="22.5" r="13.5" fill="#d6a57a" />
      <path d="M13.5 20C15 12.2 19.5 8.5 26 8.5C32.8 8.5 37.5 12.4 38.8 20C32.5 17.4 20.6 17.4 13.5 20Z" fill={colors.visor} />
      <path d={`M13.2 ${PLAYER_AVATAR_FACE.brimTopY}H38.8V${PLAYER_AVATAR_FACE.brimBottomY}C33.4 23.2 19 23.2 13.2 ${PLAYER_AVATAR_FACE.brimBottomY}Z`} fill={colors.visor} />
      <path d="M15.2 17.5C18.3 12.9 21.7 11.2 26 11.2C30.8 11.2 34.6 13.2 37 17.5Z" fill={colors.band} />
      <path d={`M15 ${PLAYER_AVATAR_FACE.brimTrimY}C20.6 22.7 31.3 22.8 37.2 ${PLAYER_AVATAR_FACE.brimTrimY}`} fill="none" stroke={colors.trim} strokeWidth="1.1" strokeLinecap="round" />
      <path d="M19.2 24.1C21 23 22.7 22.9 24.4 23.8" fill="none" stroke="#7c2d12" strokeWidth="1" strokeLinecap="round" />
      <path d="M27.6 23.8C29.3 22.9 31 23 32.8 24.1" fill="none" stroke="#7c2d12" strokeWidth="1" strokeLinecap="round" />
      <path d="M25.4 25.6C24.6 27.3 24.8 28.7 26.3 29.3" fill="none" stroke="#9a5732" strokeWidth="1" strokeLinecap="round" />
      <path d="M22 32C24.2 33.6 27.7 33.6 30 32" fill="none" stroke="#7f3f1d" strokeWidth="1.4" strokeLinecap="round" />
      <g>
        <path d={`M20 ${PLAYER_AVATAR_FACE.eyeY}C21.4 25.4 22.8 25.4 24 ${PLAYER_AVATAR_FACE.eyeY}C22.8 27 21.4 27 20 ${PLAYER_AVATAR_FACE.eyeY}Z`} fill="#0f172a" />
        <path d={`M28 ${PLAYER_AVATAR_FACE.eyeY}C29.2 25.4 30.6 25.4 32 ${PLAYER_AVATAR_FACE.eyeY}C30.6 27 29.2 27 28 ${PLAYER_AVATAR_FACE.eyeY}Z`} fill="#0f172a" />
        <circle cx="23" cy={PLAYER_AVATAR_FACE.eyeHighlightY} r="0.55" fill="#f8fafc" />
        <circle cx="31" cy={PLAYER_AVATAR_FACE.eyeHighlightY} r="0.55" fill="#f8fafc" />
      </g>
    </svg>
  );
}
